// Centralized Firestore collection name constants & related types
// Having a single source of truth helps avoid typos across the codebase.

export const COLLECTION_FREE_INTRO_PREFERRED_TIMES = 'free_intro_preferred_times';
export const COLLECTION_WAITLIST_SUBMISSIONS = 'waitlist_submissions';

// Data shape stored for preferred times request submissions
export interface FreeIntroPreferredTimesDoc {
  requested_slots: Array<{ day: string; ranges: string[] }>;
  first_name: string;
  last_name: string;
  email: string;
  message: string | null;
  created_at: any; // Firestore Timestamp (kept as any to avoid importing Firestore types here)
  source: string;
}

// Data shape for Morning Social waitlist submissions
export interface WaitlistSubmissionDoc {
  name: string;
  email: string;
  phone: string | null;
  preferred_days: string[]; // e.g., ['Monday', 'Wednesday', 'Friday']
  experience: 'never' | 'some' | 'player';
  created_at: any; // Firestore Timestamp
  source: string;
}

// Morning event registrations (v2 landing page)
export const COLLECTION_MORNING_REGISTRATIONS = 'morning_registrations';

// Weekly capacity counters (one document per ISO week, auto-created)
export const COLLECTION_MORNING_CAPACITY = 'morning_capacity';

export interface MorningRegistrationDoc {
  type: 'registration' | 'waitlist';
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  experience: 'first_timer' | 'played_few_times' | 'regular_player';
  consent: boolean;
  event_id: string;
  event_type: string;
  event_day: string;
  event_time: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  referrer: string | null;
  device_type: string;
  created_at: any; // Firestore Timestamp
  source: string;
}

// Private / corporate event inquiries
export const COLLECTION_EVENT_INQUIRIES = 'event_inquiries';

export type EventInquiryCategory = 'private' | 'corporate';
export type EventIntakeCategory = 'corporate_event' | 'private_party' | 'full_club_buyout';

export interface EventInquiryDoc {
  category: EventInquiryCategory;
  package_id: string;
  event_type: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string | null;
  headcount_range: string;
  preferred_date: string | null;
  preferred_time: string | null;
  budget_range: string | null;
  food_beverage_interest: boolean;
  message: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  referrer: string | null;
  device_type: string;
  created_at: any; // Firestore Timestamp
  source: string;
  status: 'new';
}

export interface EventIntakeDoc {
  doc_type: 'events_intake_v1';
  event_category: EventIntakeCategory;
  contact_name: string;
  email: string;
  phone: string;
  organization_name: string | null;
  intake_answers: Record<string, string | boolean | null>;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  referrer: string | null;
  device_type: string;
  created_at: any; // Firestore Timestamp
  source: 'events_page';
  status: 'new';
}

// Private coaching lesson inquiries
export const COLLECTION_COACHING_INQUIRIES = 'coaching_inquiries';

export type CoachingExperienceLevel =
  | 'new_to_padel'
  | 'beginner'
  | 'intermediate'
  | 'advanced';

export type CoachingPreferredTime =
  | 'morning'
  | 'afternoon'
  | 'evening'
  | 'flexible';

export interface CoachingInquiryDoc {
  doc_type: 'coaching_inquiry_v1';
  coach: 'Drew';
  contact_name: string;
  email: string;
  phone: string;
  experience_level: CoachingExperienceLevel;
  lesson_goals: string;
  preferred_days: string;
  preferred_time: CoachingPreferredTime;
  message: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  referrer: string | null;
  device_type: string;
  created_at: any;
  source: 'coaching_page';
  status: 'new';
}
